import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import Divider from '@segment/matcha/components/Divider'
import Button from '@segment/matcha/components/Button'
import Typography from '@segment/matcha/components/Typography'

import CardsGrid from '@segment/matcha/recipes/CardsGrid'
import CategoryHeaderBase, * as CategoryHeader from '@segment/matcha/recipes/CategoryHeader'

import type { FetchedCatalogIntegration } from '@segment/api/routes/catalog/queries'
import type { Hit } from '@segment/api/algolia/client'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import IntegrationCardTemplate from '../../../components/IntegrationCard'

interface EmptyResults {
  query: string
  entries?: Array<Hit<FetchedCatalogIntegration>>
  hitsAmount?: number
  onCategoryChange: (slug: string, state?: { query?: string }) => void
}

const EmptyResults: FC<PropsWithChildren<EmptyResults>> = ({ query, onCategoryChange, entries, hitsAmount }) => {
  const pageConfig = usePageConfig()
  const hasEntries = entries ? entries.length > 0 : undefined
  const moreHitsAvailable = hitsAmount && hitsAmount > 4

  return (
    <>
      <NoMarginTypography variant="pMedium">Sorry, we couldn’t find anything that fit your search.</NoMarginTypography>

      {hasEntries && (
        <>
          <Divider />

          <CategoryHeaderBase>
            <CategoryHeader.Title variant="h4">
              {hitsAmount ?? null} search results for "{query}" in All categories
            </CategoryHeader.Title>
            {moreHitsAvailable && (
              <CategoryHeader.Button onClick={() => onCategoryChange(`all`)}>View all</CategoryHeader.Button>
            )}
          </CategoryHeaderBase>
          <CardsGrid limitItems={false}>
            {entries?.map((integration) => (
              <IntegrationCardTemplate key={integration.objectID} integration={integration} />
            ))}
          </CardsGrid>
        </>
      )}

      <Divider />

      <Grid>
        <NoMarginTypography variant="h4" gridArea="heading1">
          Can't find what you're looking for?
        </NoMarginTypography>

        <NoMarginTypography variant="h4" gridArea="heading2">
          Interested in partnering with us?
        </NoMarginTypography>

        <NoMarginTypography variant="pMedium" gridArea="text1">
          Let us know and we’ll help you set up the integration you need.
        </NoMarginTypography>

        <NoMarginTypography variant="pMedium" gridArea="text2">
          Enable thousands of companies to instantly activate their first-party data in your app.
        </NoMarginTypography>

        <ButtonWrapper>
          <Button
            tag="a"
            href={getAbsoluteUrl(`/requests/integrations/`, pageConfig)}
            size="medium"
            color="primary"
            variant="filled"
          >
            Request a new integration
          </Button>
        </ButtonWrapper>

        <ButtonWrapper>
          <Button
            tag="a"
            href={getAbsoluteUrl(`/partners/developer-portal/`, pageConfig)}
            size="medium"
            color="primary"
            variant="filled"
          >
            Visit our Developer Portal
          </Button>
        </ButtonWrapper>
      </Grid>
    </>
  )
}

export default EmptyResults

type NoMarginTypographyProps = {
  gridArea?: string
}

const NoMarginTypography = styled(Typography)<NoMarginTypographyProps>`
  margin: 0;
  grid-area: ${({ gridArea }) => gridArea};
`

const Grid = styled.div`
  display: grid;
  row-gap: 16px;
  column-gap: 80px;
  grid-template-areas:
    'heading1'
    'text1'
    'button1'
    'heading2'
    'text2'
    'button2';

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    grid-template-areas:
      'heading1 heading2'
      'text1 text2'
      'button1 button2';
  }

  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    margin-right: 160px;
  }
`

const ButtonWrapper = styled.div`
  margin-top: 14px;
  margin-bottom: 24px;
`
